import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { FaqAccordion } from '../components/FaqAccordion';

/* ------------------------------------------------------------------ */
/*  SVG Icons (inline, no external deps)                               */
/* ------------------------------------------------------------------ */

function AirplaneIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.4-.1.9.3 1.1L11 12l-2 3H6l-1 1 3 2 2 3 1-1v-3l3-2 3.7 7.3c.2.4.7.5 1.1.3l.5-.3c.4-.2.6-.6.5-1.1z" />
    </svg>
  );
}

function ChartIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M7 16l4-8 4 4 5-9" />
    </svg>
  );
}

function BrainIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a6 6 0 0 0-6 6c0 1.6.6 3 1.7 4.1L12 17l4.3-4.9A6 6 0 0 0 12 2z" />
      <circle cx="12" cy="8" r="2" />
      <path d="M12 17v5" />
      <path d="M8 21h8" />
    </svg>
  );
}

function BellIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function TargetIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

function DevicesIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  );
}

function MicIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v4" />
      <path d="M8 21h8" />
    </svg>
  );
}

function ChatIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <circle cx="9" cy="10" r="0.5" fill="currentColor" />
      <circle cx="13" cy="10" r="0.5" fill="currentColor" />
      <circle cx="17" cy="10" r="0.5" fill="currentColor" />
    </svg>
  );
}

function CheckIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export function Landing() {
  const { t } = useTranslation();

  const STEPS = [
    {
      icon: AirplaneIcon,
      title: t('landing.howItWorks.step1Title'),
      description: t('landing.howItWorks.step1Description'),
      color: 'bg-blue-100 text-blue-600',
    },
    {
      icon: ChartIcon,
      title: t('landing.howItWorks.step2Title'),
      description: t('landing.howItWorks.step2Description'),
      color: 'bg-purple-100 text-purple-600',
    },
    {
      icon: BrainIcon,
      title: t('landing.howItWorks.step3Title'),
      description: t('landing.howItWorks.step3Description'),
      color: 'bg-emerald-100 text-emerald-600',
    },
  ] as const;

  const FEATURES = [
    {
      icon: BellIcon,
      title: t('landing.features.smartNotifTitle'),
      description: t('landing.features.smartNotifDesc'),
      badges: [
        { label: t('landing.features.badgeGood'), variant: 'good' as const },
        { label: t('landing.features.badgeRare'), variant: 'rare' as const },
        { label: t('landing.features.badgeExceptional'), variant: 'exceptional' as const },
      ],
    },
    {
      icon: ChartIcon,
      title: t('landing.features.predictiveTitle'),
      description: t('landing.features.predictiveDesc'),
    },
    {
      icon: TargetIcon,
      title: t('landing.features.thresholdsTitle'),
      description: t('landing.features.thresholdsDesc'),
    },
    {
      icon: DevicesIcon,
      title: t('landing.features.multichannelTitle'),
      description: t('landing.features.multichannelDesc'),
    },
    {
      icon: MicIcon,
      title: t('landing.features.voiceTitle'),
      description: t('landing.features.voiceDesc'),
    },
    {
      icon: ChatIcon,
      title: t('landing.features.chatTitle'),
      description: t('landing.features.chatDesc'),
    },
  ] as const;

  const PLANS = [
    {
      name: t('landing.pricing.freeName'),
      price: '0',
      period: t('landing.pricing.forever'),
      features: [
        t('pricing.free.monitor'),
        t('pricing.free.notifications'),
        t('pricing.free.history'),
      ],
      cta: t('landing.pricing.ctaFree'),
      ctaLink: '/register',
      highlighted: false,
    },
    {
      name: t('landing.pricing.proName'),
      price: '0,99',
      period: t('landing.pricing.perMonth'),
      features: [
        t('pricing.pro.monitors'),
        t('pricing.pro.notifications'),
        t('pricing.pro.history'),
        t('pricing.pro.predictive'),
        t('pricing.pro.aiCompile'),
      ],
      cta: t('landing.pricing.ctaPro'),
      ctaLink: '/register',
      highlighted: false,
    },
    {
      name: t('landing.pricing.unlimitedName'),
      price: '4,99',
      period: t('landing.pricing.perMonth'),
      features: [
        t('pricing.unlimited.monitors'),
        t('pricing.unlimited.notifications'),
        t('pricing.unlimited.history'),
        t('pricing.unlimited.predictive'),
        t('pricing.unlimited.aiCompile'),
        t('pricing.unlimited.voiceAgent'),
      ],
      cta: t('landing.pricing.ctaUnlimited'),
      ctaLink: '/register',
      highlighted: true,
    },
  ] as const;

  const FAQ_ITEMS = [
    { question: t('landing.faq.q1'), answer: t('landing.faq.a1') },
    { question: t('landing.faq.q2'), answer: t('landing.faq.a2') },
    { question: t('landing.faq.q3'), answer: t('landing.faq.a3') },
    { question: t('landing.faq.q4'), answer: t('landing.faq.a4') },
    { question: t('landing.faq.q5'), answer: t('landing.faq.a5') },
    { question: t('landing.faq.q6'), answer: t('landing.faq.a6') },
  ];

  const NOTIF_EXAMPLES = [
    {
      variant: 'good' as const,
      label: t('landing.notifications.labelGood'),
      from: 'Roma',
      to: 'Barcellona',
      price: '79',
      deltaPct: t('landing.notifications.deltaGood'),
      cta: t('landing.notifications.ctaGood'),
    },
    {
      variant: 'rare' as const,
      label: t('landing.notifications.labelRare'),
      from: 'Milano',
      to: 'New York',
      price: '249',
      deltaPct: t('landing.notifications.deltaRare'),
      cta: t('landing.notifications.ctaRare'),
    },
    {
      variant: 'exceptional' as const,
      label: t('landing.notifications.labelExceptional'),
      from: 'Milano',
      to: 'Tokyo',
      price: '430',
      deltaPct: t('landing.notifications.deltaExceptional'),
      cta: t('landing.notifications.ctaExceptional'),
    },
  ];

  const priceTemplate = t('landing.notifications.priceInLastMonths');

  return (
    <div className="overflow-x-hidden">
      {/* ============================================================ */}
      {/*  HERO                                                         */}
      {/* ============================================================ */}
      <section className="relative isolate px-4 pb-20 pt-16 md:px-6 md:pb-28 md:pt-24">
        {/* Background decoration */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          {/* Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-surface to-blue-50" />
          {/* Decorative circles */}
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary-100/40 blur-3xl" />
          <div className="absolute -left-20 top-1/2 h-60 w-60 rounded-full bg-blue-100/30 blur-3xl" />
          {/* Dotted pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          {/* Airplane trail */}
          <svg className="absolute right-[10%] top-[15%] h-40 w-40 text-primary-200 opacity-60 md:h-56 md:w-56" viewBox="0 0 200 200" fill="none" aria-hidden="true">
            <path d="M20 180 Q 60 60, 180 20" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" fill="none" />
            <path d="M170 10 l12 8-4 14" stroke="currentColor" strokeWidth="2" fill="none" />
          </svg>
        </div>

        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl md:text-5xl lg:text-6xl">
            {t('landing.hero.titlePrefix')}
            <span className="bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">
              {t('landing.hero.titleHighlight')}
            </span>
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-text-secondary md:mt-6 md:text-lg">
            {t('landing.hero.subtitle')}
          </p>

          <div className="mt-8 flex flex-col items-center gap-5 md:mt-10">
            <Link to="/register">
              <Button size="lg">{t('landing.hero.cta')}</Button>
            </Link>

            <div className="flex flex-col items-center gap-3 sm:flex-row">
              <a
                href="#"
                aria-label={t('landing.hero.appStoreAria')}
                className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2.5 text-white shadow-sm transition-transform hover:scale-[1.02]"
              >
                <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <div className="text-left leading-tight">
                  <div className="text-[10px] font-medium opacity-80">{t('landing.hero.downloadOn')}</div>
                  <div className="text-base font-semibold">App Store</div>
                </div>
              </a>
              <a
                href="#"
                aria-label={t('landing.hero.googlePlayAria')}
                className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2.5 text-white shadow-sm transition-transform hover:scale-[1.02]"
              >
                <svg className="h-7 w-7" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3.6 2.4c-.3.3-.5.7-.5 1.3v16.6c0 .6.2 1 .5 1.3l9.5-9.6L3.6 2.4z" fill="#00d4ff" />
                  <path d="M16.4 8.5L4.6 1.7l8.5 8.6 3.3-1.8z" fill="#00f076" />
                  <path d="M20.5 11.1l-4.1-2.6-3.6 3.6 3.6 3.6 4.1-2.4c1.2-.7 1.2-1.5 0-2.2z" fill="#ffd233" />
                  <path d="M4.6 22.3l11.8-6.6-3.3-1.8-8.5 8.4z" fill="#ff3a44" />
                </svg>
                <div className="text-left leading-tight">
                  <div className="text-[10px] font-medium opacity-80">{t('landing.hero.availableOn')}</div>
                  <div className="text-base font-semibold">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  COME FUNZIONA                                                */}
      {/* ============================================================ */}
      <section id="come-funziona" className="scroll-mt-20 px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-2xl font-bold text-text-primary md:text-3xl">
            {t('landing.howItWorks.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-text-secondary md:text-base">
            {t('landing.howItWorks.subtitle')}
          </p>

          <div className="mt-12 grid gap-8 sm:grid-cols-3 md:mt-16">
            {STEPS.map((step, i) => (
              <div key={step.title} className="flex flex-col items-center text-center">
                {/* Step number + icon */}
                <div className="relative mb-5">
                  <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${step.color}`}>
                    <step.icon className="h-7 w-7" />
                  </div>
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                    {i + 1}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-text-primary">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURE HIGHLIGHTS                                           */}
      {/* ============================================================ */}
      <section className="bg-surface-secondary px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-2xl font-bold text-text-primary md:text-3xl">
            {t('landing.features.heading')}
          </h2>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 md:mt-16">
            {FEATURES.map((feature) => (
              <Card key={feature.title} padding="lg" variant="default" className="transition-shadow hover:shadow-md">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-text-primary">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">{feature.description}</p>
                {'badges' in feature && feature.badges && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {feature.badges.map((b) => (
                      <Badge key={b.label} variant={b.variant} size="sm">{b.label}</Badge>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  PRICING                                                      */}
      {/* ============================================================ */}
      <section id="pricing" className="scroll-mt-20 px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-2xl font-bold text-text-primary md:text-3xl">
            {t('landing.pricing.heading')}
          </h2>

          <div className="mt-12 grid gap-6 sm:grid-cols-3 md:mt-16">
            {PLANS.map((plan) => (
              <Card
                key={plan.name}
                variant={plan.highlighted ? 'bordered' : 'default'}
                padding="lg"
                className={`relative flex flex-col ${plan.highlighted ? 'ring-2 ring-primary-500 shadow-lg' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge variant="info">{t('landing.pricing.mostPopular')}</Badge>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-text-primary">{plan.name}</h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-3xl font-extrabold text-text-primary">{plan.price}&euro;</span>
                    <span className="text-sm text-text-secondary">/{plan.period}</span>
                  </div>
                </div>

                <ul className="mb-8 flex-1 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-text-secondary">
                      <CheckIcon className="h-4 w-4 shrink-0 text-primary-600" />
                      {f}
                    </li>
                  ))}
                </ul>

                <Link to={plan.ctaLink}>
                  <Button
                    variant={plan.highlighted ? 'primary' : 'outline'}
                    size="md"
                    className="w-full"
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  NOTIFICATION EXAMPLES                                        */}
      {/* ============================================================ */}
      <section className="bg-surface-secondary px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-2xl font-bold text-text-primary md:text-3xl">
            {t('landing.notifications.heading')}
          </h2>

          <div className="mx-auto mt-10 grid max-w-5xl gap-6 md:mt-14 md:grid-cols-3">
            {NOTIF_EXAMPLES.map((n) => {
              const [before, after] = priceTemplate.split('{delta}');
              return (
                <Card key={n.label} variant="elevated" padding="lg" className="relative">
                  <Badge variant={n.variant}>{n.label}</Badge>

                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-lg font-bold text-text-primary">{n.from}</span>
                    <AirplaneIcon className="h-5 w-5 text-primary-500" />
                    <span className="text-lg font-bold text-text-primary">{n.to}</span>
                    <span className="ml-auto text-xl font-extrabold text-primary-600">{n.price}&euro;</span>
                  </div>

                  <p className="mt-3 text-sm text-text-secondary">
                    {before}<strong className="text-text-primary">{n.deltaPct}</strong>{after}
                  </p>

                  <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2">
                    <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />
                    <span className="text-sm font-medium text-green-700">{n.cta}</span>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FAQ                                                          */}
      {/* ============================================================ */}
      <section className="px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center text-2xl font-bold text-text-primary md:text-3xl">
            {t('landing.faq.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-text-secondary md:text-base">
            {t('landing.faq.subtitle')}
          </p>

          <div className="mt-10 md:mt-14">
            <FaqAccordion items={FAQ_ITEMS} />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FINAL CTA                                                    */}
      {/* ============================================================ */}
      <section className="relative isolate px-4 py-16 md:px-6 md:py-24">
        {/* Background */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-primary-600 to-blue-700" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }} />

        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-white md:text-3xl">
            {t('landing.finalCta.title')}
          </h2>
          <p className="mt-3 text-base text-white/80 md:text-lg">
            {t('landing.finalCta.subtitle')}
          </p>
          <div className="mt-8">
            <Link to="/register">
              <button
                className="inline-flex items-center justify-center rounded-lg px-8 py-3 text-base font-bold shadow-lg transition-all"
                style={{ backgroundColor: '#ffffff', color: '#4338ca' }}
              >
                {t('landing.hero.cta')}
              </button>
            </Link>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2">
            <span className="text-sm text-white/70">{t('landing.finalCta.followUs')}</span>
            <a
              href="https://www.instagram.com/flymind.travel"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Instagram"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
            <a
              href="https://t.me/flymindtravel"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Telegram"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/joinflymind"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Facebook"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
